import * as React from "react"
import { Head, Link, router } from "@inertiajs/react"
import { useIntl } from "react-intl"
import { ArrowLeft, Layers, ListChecks, Pencil, Trash2 } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { ConfirmDialog } from "@/components/flashcards/ConfirmDialog"
import { SOURCE_KIND_LABELS } from "@/components/generations/format"
import { Button } from "@/components/ui/button"
import { RenameSourceDialog } from "@/components/generations/RenameSourceDialog"
import { useFormat } from "@/lib/format"
import type { SourceDetail } from "@/types/generations"

export default function SourceShow({ source }: { source: SourceDetail }) {
  const intl = useIntl()
  const { date, wordCount } = useFormat()
  const [renaming, setRenaming] = React.useState(false)
  const [deleting, setDeleting] = React.useState(false)
  const description = intl.formatMessage({ id: "sources.show_meta" }, { title: source.title })

  return (
    <>
      <Head title={source.title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={source.title} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell>
        <Link href="/sources" className="mb-4 inline-flex items-center gap-1 text-sm">
          <ArrowLeft className="h-4 w-4" /> {intl.formatMessage({ id: "sources.title" })}
        </Link>
        <PageHeader
          title={source.title}
          description={
            <>
              {intl.formatMessage(SOURCE_KIND_LABELS[source.kind])} · {wordCount(source.word_count)} · {date(source.created_on, { month: "short", day: "numeric", year: "numeric" })}
              {source.kind === "url" && source.link_or_file_name && (
                <>
                  {" · "}
                  <a href={source.link_or_file_name} target="_blank" rel="noopener noreferrer nofollow">
                    {intl.formatMessage({ id: "sources.open_link" })}
                  </a>
                </>
              )}
              {source.kind === "file" && source.link_or_file_name && ` · ${source.link_or_file_name}`}
            </>
          }
          actions={
            <>
              <Button type="button" variant="ghost" size="icon" aria-label={intl.formatMessage({ id: "sources.rename" })} onClick={() => setRenaming(true)}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button type="button" variant="ghost" size="icon" aria-label={intl.formatMessage({ id: "sources.delete" })} onClick={() => setDeleting(true)}>
                <Trash2 className="h-4 w-4" />
              </Button>
              <Button asChild variant="secondary">
                <Link href={`/generations/new?kind=deck&source_id=${source.id}`}>
                  <Layers className="h-4 w-4" /> {intl.formatMessage({ id: "sources.generate_deck_short" })}
                </Link>
              </Button>
              <Button asChild>
                <Link href={`/generations/new?kind=quiz&source_id=${source.id}`}>
                  <ListChecks className="h-4 w-4" /> {intl.formatMessage({ id: "sources.generate_quiz_short" })}
                </Link>
              </Button>
            </>
          }
        />

        <section className="mt-8">
          <h2>{intl.formatMessage({ id: "sources.extracted_heading" })}</h2>
          <p className="mt-1 text-sm text-ink-muted">{intl.formatMessage({ id: "sources.extracted_hint" })}</p>
          <div className="callout mt-4 max-h-[32rem] overflow-y-auto">
            <p className="whitespace-pre-wrap break-words">{source.extracted_text}</p>
          </div>
        </section>

        <RenameSourceDialog source={source} open={renaming} onOpenChange={setRenaming} />
        <ConfirmDialog
          open={deleting}
          onOpenChange={setDeleting}
          title={intl.formatMessage({ id: "sources.delete_title" })}
          description={intl.formatMessage({ id: "sources.delete_body" })}
          confirmLabel={intl.formatMessage({ id: "sources.delete" })}
          onConfirm={() => router.delete(`/sources/${source.id}`)}
        />
      </AppShell>
    </>
  )
}
